import React, { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Search, X, MousePointerClick, Database, Users } from 'lucide-react';
import axiosInstance from 'shared/services/api/axiosInstance';
import { activityFilterPopupTheme as fp } from 'shared/theme/activityFilterPopupTheme';
import { primary as PRIMARY } from 'shared/theme/trodoColors';
import { GROUP_STANDARD_PROPERTIES, getDefaultGroupOperator } from 'features/activity/config/groupFilterProperties';
import { DEFAULT_WHO_TIME_FILTER } from 'features/activity/utils/whoTimeFilterUtils';

function generateId() {
  return `gf-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function mapApiPropType(t) {
  if (!t) return 'string';
  const x = String(t).toLowerCase();
  if (x === 'array') return 'list';
  return x;
}

/**
 * Group filter picker: Events (WHO) / Properties (WHERE) / Cohorts — matches
 * the agent-runs PropertyPickerPopover design language: clean nav, monospace
 * labels, uppercase type tags, no per-row icons.
 */
const GroupPropertyFilterPopup = ({
  isOpen,
  onClose,
  triggerRef,
  teamId,
  groupKey,
  existingFilter,
  onAdd,
}) => {
  const [selectedNavItem, setSelectedNavItem] = useState('events');
  const [searchTerm, setSearchTerm] = useState('');
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [groupProperties, setGroupProperties] = useState([]);
  const [eventNames, setEventNames] = useState([]);
  const [cohorts, setCohorts] = useState([]);
  const [loadingProps, setLoadingProps] = useState(false);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [loadingCohorts, setLoadingCohorts] = useState(false);
  const popupRef = useRef(null);

  const getNumericTeamId = () => {
    let id = typeof teamId === 'object' && teamId !== null
      ? parseInt(teamId._id || teamId.id, 10)
      : parseInt(teamId, 10);
    if (typeof teamId === 'string' && Number.isNaN(id)) {
      try {
        const td = JSON.parse(localStorage.getItem('selectedTeamData') || '{}');
        id = parseInt(td._id || td.id, 10);
      } catch (_) {
        /* ignore */
      }
    }
    return Number.isNaN(id) ? null : id;
  };

  useEffect(() => {
    if (isOpen && triggerRef?.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const scrollY = window.scrollY || window.pageYOffset;
      const scrollX = window.scrollX || window.pageXOffset;
      setPosition({
        top: rect.bottom + scrollY + 8,
        left: rect.left + scrollX,
      });
    }
  }, [isOpen, triggerRef]);

  useEffect(() => {
    if (!isOpen) return;
    if (existingFilter) {
      const src = existingFilter.propertySource || existingFilter.category;
      if (src === 'event') setSelectedNavItem('events');
      else if (src === 'cohort') setSelectedNavItem('cohorts');
      else setSelectedNavItem('properties');
    } else {
      setSelectedNavItem('events');
    }
    setSearchTerm('');
  }, [isOpen, existingFilter]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        isOpen &&
        popupRef.current &&
        !popupRef.current.contains(e.target) &&
        triggerRef?.current &&
        !triggerRef.current.contains(e.target)
      ) {
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen, onClose, triggerRef]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) onClose();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen || selectedNavItem !== 'properties' || !teamId || !groupKey) return;
    setLoadingProps(true);
    axiosInstance
      .get('/group-identities/properties', { params: { team_id: teamId, group_key: groupKey } })
      .then((r) => {
        const raw = r.data?.properties || [];
        const custom = raw.map((item) => {
          const key = typeof item === 'string' ? item : item.key;
          const type = typeof item === 'object' && item ? mapApiPropType(item.type) : 'string';
          return { name: key, label: key, type, source: 'group' };
        });
        const stdNames = new Set(GROUP_STANDARD_PROPERTIES.map((p) => p.name));
        const merged = [
          ...GROUP_STANDARD_PROPERTIES.map((p) => ({
            name: p.name, label: p.label, type: p.type, source: 'group', isStandard: true,
          })),
          ...custom.filter((p) => p.name && !stdNames.has(p.name)),
        ].sort((a, b) => (a.label || '').localeCompare(b.label || ''));
        setGroupProperties(merged);
      })
      .catch(() => setGroupProperties(
        GROUP_STANDARD_PROPERTIES.map((p) => ({
          name: p.name, label: p.label, type: p.type, source: 'group', isStandard: true,
        }))
      ))
      .finally(() => setLoadingProps(false));
  }, [isOpen, selectedNavItem, teamId, groupKey]);

  useEffect(() => {
    if (!isOpen || selectedNavItem !== 'events') return;
    const numericTeamId = getNumericTeamId();
    if (!numericTeamId) return;
    setLoadingEvents(true);
    axiosInstance
      .get('/events/summaries', { params: { team_id: numericTeamId } })
      .then((res) => {
        const summaries = res.data?.summaries || [];
        const names = [...new Set(summaries.map((s) => s.event_name).filter(Boolean))].sort();
        setEventNames(names);
      })
      .catch(() => setEventNames([]))
      .finally(() => setLoadingEvents(false));
  }, [isOpen, selectedNavItem, teamId]);

  useEffect(() => {
    if (!isOpen || selectedNavItem !== 'cohorts') return;
    const numericTeamId = getNumericTeamId();
    if (!numericTeamId) return;
    setLoadingCohorts(true);
    axiosInstance
      .get('/cohorts', { params: { team_id: numericTeamId, include_user_count: false } })
      .then((res) => {
        if (res.data?.success && Array.isArray(res.data.cohorts)) {
          setCohorts(res.data.cohorts);
        } else {
          setCohorts([]);
        }
      })
      .catch(() => setCohorts([]))
      .finally(() => setLoadingCohorts(false));
  }, [isOpen, selectedNavItem, teamId]);

  const handleGroupPropertyClick = (prop) => {
    const newFilter = {
      id: existingFilter?.id || generateId(),
      category: 'group',
      propertySource: 'group',
      connector: existingFilter?.connector,
      field: prop.name,
      property: prop.name,
      propertyLabel: prop.label || prop.name,
      propertyType: prop.type || 'string',
      operator: getDefaultGroupOperator(prop.type || 'string'),
      value: null,
      value2: null,
    };
    onAdd(newFilter);
    onClose();
  };

  const handleEventClick = (eventLabel) => {
    const field = `event_count:${eventLabel}`;
    const newFilter = {
      id: existingFilter?.id || generateId(),
      propertySource: 'event',
      category: 'event',
      connector: existingFilter?.connector,
      field,
      property: field,
      propertyLabel: eventLabel,
      propertyType: 'number',
      operator: 'greater_than',
      value: 1,
      value2: null,
      didOperator: existingFilter?.didOperator || 'did',
      eventTimeFilter: existingFilter?.eventTimeFilter
        ? { ...existingFilter.eventTimeFilter }
        : { ...DEFAULT_WHO_TIME_FILTER },
    };
    onAdd(newFilter);
    onClose();
  };

  const handleCohortClick = (cohort) => {
    onAdd({
      id: existingFilter?.id || generateId(),
      category: 'cohort',
      propertySource: 'cohort',
      field: 'cohort',
      value: [cohort.id],
      propertyLabel: cohort.name,
      operator: 'is',
      connector: existingFilter?.connector ?? 'AND',
    });
    onClose();
  };

  const filteredEvents = useMemo(() => eventNames.filter(
    (n) => !searchTerm || n.toLowerCase().includes(searchTerm.toLowerCase())
  ), [eventNames, searchTerm]);
  const filteredGroupProps = useMemo(() => groupProperties.filter(
    (p) =>
      (p.label || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (p.name || '').toLowerCase().includes(searchTerm.toLowerCase())
  ), [groupProperties, searchTerm]);
  const filteredCohorts = useMemo(() => cohorts.filter(
    (c) => !searchTerm || (c.name || '').toLowerCase().includes(searchTerm.toLowerCase())
  ), [cohorts, searchTerm]);

  const tabs = [
    { id: 'events',     label: 'Events',     Icon: MousePointerClick },
    { id: 'properties', label: 'Properties', Icon: Database },
    { id: 'cohorts',    label: 'Cohorts',    Icon: Users },
  ];

  if (!isOpen) return null;

  const placeholder =
    selectedNavItem === 'events'
      ? 'Search events…'
      : selectedNavItem === 'properties'
        ? 'Search properties…'
        : 'Search cohorts…';

  return createPortal(
    <div
      ref={popupRef}
      className="fixed z-[9999] flex flex-col sm:flex-row overflow-hidden rounded-xl border bg-surface shadow-2xl"
      style={{
        top: `${position.top}px`,
        left: `${position.left}px`,
        width: 460,
        maxHeight: 420,
        borderColor: fp.border,
        fontFamily: 'Inter, sans-serif',
      }}
    >
      {/* Left nav */}
      <div
        className="flex w-full sm:w-[140px] flex-shrink-0 flex-row sm:flex-col border-b sm:border-b-0 sm:border-r overflow-x-auto sm:overflow-x-visible"
        style={{ borderColor: fp.border, backgroundColor: fp.bgNav }}
      >
        <div
          className="hidden sm:block border-b px-3 py-2 text-[10px] font-semibold uppercase tracking-wide text-[color:var(--text-muted)]"
          style={{ borderColor: fp.border }}
        >
          Categories
        </div>
        <div className="flex flex-1 sm:flex-col flex-row overflow-x-auto sm:overflow-x-visible sm:overflow-y-auto py-1">
          {tabs.map((t) => {
            const Icon = t.Icon;
            const active = selectedNavItem === t.id;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => { setSelectedNavItem(t.id); setSearchTerm(''); }}
                className="flex flex-shrink-0 sm:w-full w-auto items-center gap-2 px-3 py-2 text-left text-[13px] transition-colors whitespace-nowrap"
                style={{
                  backgroundColor: active ? fp.navActiveBg : 'transparent',
                  color: active ? PRIMARY : 'var(--text-secondary)',
                  fontWeight: active ? 600 : 500,
                }}
              >
                <Icon size={14} style={{ color: active ? PRIMARY : 'var(--text-disabled)' }} />
                {t.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Right pane */}
      <div className="flex flex-1 flex-col min-w-0">
        {/* Search */}
        <div className="flex items-center gap-2 border-b px-3 py-2" style={{ borderColor: fp.border }}>
          <Search size={14} className="text-[color:var(--text-muted)]" />
          <input
            autoFocus
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={placeholder}
            className="flex-1 bg-transparent text-[13px] text-[color:var(--text-primary)] placeholder:text-[color:var(--text-muted)] focus:outline-none"
          />
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-[color:var(--text-muted)] hover:bg-surface-hover hover:text-[color:var(--text-primary)]"
            title="Close"
          >
            <X size={14} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto">
          {selectedNavItem === 'cohorts' ? (
            loadingCohorts ? (
              <div className="px-3 py-6 text-[12px] text-center text-[color:var(--text-muted)]">Loading cohorts…</div>
            ) : cohorts.length === 0 ? (
              <div className="px-3 py-6 text-[12px] text-center text-[color:var(--text-muted)]">No cohorts. Create cohorts in the Cohorts page.</div>
            ) : (
              filteredCohorts.map((cohort) => (
                <button
                  key={cohort.id}
                  type="button"
                  onClick={() => handleCohortClick(cohort)}
                  className="flex w-full items-center justify-between px-3 py-1.5 text-left text-[12px] transition-colors hover:bg-[color:var(--surface-sunken)]"
                >
                  <span className="font-mono text-[color:var(--text-primary)] truncate">{cohort.name}</span>
                  <span className="ml-2 text-[10px] uppercase text-[color:var(--text-muted)] flex-shrink-0">cohort</span>
                </button>
              ))
            )
          ) : selectedNavItem === 'events' ? (
            loadingEvents ? (
              <div className="px-3 py-6 text-[12px] text-center text-[color:var(--text-muted)]">Loading…</div>
            ) : filteredEvents.length === 0 ? (
              <div className="px-3 py-6 text-[12px] text-center text-[color:var(--text-muted)]">
                {searchTerm ? 'No events match.' : 'No events.'}
              </div>
            ) : (
              filteredEvents.map((name) => {
                const field = `event_count:${name}`;
                const isSelected =
                  existingFilter && (existingFilter.field === field || existingFilter.property === field);
                return (
                  <button
                    key={name}
                    type="button"
                    onClick={() => handleEventClick(name)}
                    className="flex w-full items-center justify-between px-3 py-1.5 text-left text-[12px] transition-colors"
                    style={{ backgroundColor: isSelected ? fp.rowHoverBg : 'transparent' }}
                    onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.backgroundColor = 'var(--surface-sunken)'; }}
                    onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.backgroundColor = 'transparent'; }}
                  >
                    <span className="font-mono text-[color:var(--text-primary)] truncate">{name}</span>
                    <span className="ml-2 inline-flex items-center gap-1.5 text-[10px] uppercase text-[color:var(--text-muted)] flex-shrink-0">
                      {isSelected && <span style={{ color: PRIMARY }}>current</span>}
                      event
                    </span>
                  </button>
                );
              })
            )
          ) : loadingProps ? (
            <div className="px-3 py-6 text-[12px] text-center text-[color:var(--text-muted)]">Loading…</div>
          ) : filteredGroupProps.length === 0 ? (
            <div className="px-3 py-6 text-[12px] text-center text-[color:var(--text-muted)]">
              {searchTerm ? 'No properties match.' : 'No properties.'}
            </div>
          ) : (
            filteredGroupProps.map((prop, idx) => {
              const isSelected =
                existingFilter &&
                (existingFilter.field === prop.name || existingFilter.property === prop.name);
              return (
                <button
                  key={`${prop.name}-${idx}`}
                  type="button"
                  onClick={() => handleGroupPropertyClick(prop)}
                  className="flex w-full items-center justify-between px-3 py-1.5 text-left text-[12px] transition-colors"
                  style={{ backgroundColor: isSelected ? fp.rowHoverBg : 'transparent' }}
                  onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.backgroundColor = 'var(--surface-sunken)'; }}
                  onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.backgroundColor = 'transparent'; }}
                >
                  <span className="font-mono text-[color:var(--text-primary)] truncate">{prop.label}</span>
                  <span className="ml-2 inline-flex items-center gap-1.5 text-[10px] uppercase text-[color:var(--text-muted)] flex-shrink-0">
                    {isSelected && <span style={{ color: PRIMARY }}>current</span>}
                    {prop.type || 'string'}
                  </span>
                </button>
              );
            })
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default GroupPropertyFilterPopup;
